/**
 * PendingConnectionRequests Component
 *
 * Component for viewing and managing pending connection requests.
 * Shows both received requests (to accept/decline) and sent requests (to cancel).
 * Includes expiry warnings and optimistic UI updates.
 */

import React, { useEffect } from 'react';
import {
  UserCheck,
  UserX,
  X,
  Clock,
  AlertCircle,
  Loader2,
  Send,
  Inbox,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  usePendingReceived,
  usePendingSent,
  usePendingReceivedCount,
  usePendingSentCount,
  useConnectionLoading,
  useConnectionActions,
} from '@/stores/connection';
import type {
  PendingConnectionRequest,
  SentConnectionRequest,
} from '@/types/connections';

interface PendingConnectionRequestsProps {
  defaultTab?: 'received' | 'sent';
  onTabChange?: (tab: string) => void;
}

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Calculate expiry info for a request
 */
const getExpiryInfo = (expiresAt: string) => {
  const expiry = new Date(expiresAt);
  const now = new Date();
  const hoursRemaining = Math.floor(
    (expiry.getTime() - now.getTime()) / (1000 * 60 * 60)
  );
  const daysRemaining = Math.floor(hoursRemaining / 24);

  const isExpiringSoon = hoursRemaining < 24 && hoursRemaining > 0;
  const isExpired = hoursRemaining <= 0;

  let expiryText = '';
  if (isExpired) {
    expiryText = 'Expired';
  } else if (hoursRemaining < 1) {
    expiryText = 'Expires in less than 1 hour';
  } else if (hoursRemaining < 24) {
    expiryText = `Expires in ${hoursRemaining} hour${hoursRemaining !== 1 ? 's' : ''}`;
  } else {
    expiryText = `Expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
  }

  return { isExpiringSoon, isExpired, expiryText };
};

/**
 * Format request date
 */
const formatRequestDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * ReceivedRequestCard - Card for received connection requests
 */
interface ReceivedRequestCardProps {
  request: PendingConnectionRequest;
  onAccept: () => void;
  onDecline: () => void;
  isActioning: boolean;
}

const ReceivedRequestCard: React.FC<ReceivedRequestCardProps> = ({
  request,
  onAccept,
  onDecline,
  isActioning,
}) => {
  const { requester, message, requestedAt, expiresAt } = request;
  const expiryInfo = getExpiryInfo(expiresAt);

  const displayName = requester.firstName && requester.lastName
    ? `${requester.firstName} ${requester.lastName}`
    : requester.username;

  const initials = getInitials(
    requester.firstName,
    requester.lastName,
    requester.username
  );

  return (
    <div
      className={`relative flex flex-col items-center p-4 rounded-lg border bg-card shadow-sm hover:shadow-md transition-all duration-200 ${
        expiryInfo.isExpired
          ? 'border-destructive/50 bg-destructive/5'
          : expiryInfo.isExpiringSoon
            ? 'border-yellow-500/50 bg-yellow-50/50 dark:bg-yellow-950/20'
            : ''
      }`}
    >
      {/* Avatar */}
      <Avatar className="h-14 w-14 ring-2 ring-background shadow mb-2">
        <AvatarImage src={requester.avatar} alt={displayName} />
        <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
          {initials}
        </AvatarFallback>
      </Avatar>

      {/* Name and username */}
      <div className="text-center mb-1">
        <h3 className="font-medium text-sm line-clamp-1">{displayName}</h3>
        <p className="text-xs text-muted-foreground">@{requester.username}</p>
      </div>

      {/* Request date badge */}
      <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 mb-1">
        <Clock className="h-2.5 w-2.5 mr-1" />
        {formatRequestDate(requestedAt)}
      </Badge>

      {/* Expiry badge */}
      <Badge
        variant="outline"
        className={`text-[10px] px-1.5 py-0.5 mb-2 ${
          expiryInfo.isExpired
            ? 'border-destructive/50 text-destructive'
            : expiryInfo.isExpiringSoon
              ? 'border-yellow-500/50 text-yellow-600 dark:text-yellow-500'
              : ''
        }`}
      >
        {(expiryInfo.isExpired || expiryInfo.isExpiringSoon) && (
          <AlertCircle className="h-2.5 w-2.5 mr-1" />
        )}
        {expiryInfo.expiryText}
      </Badge>

      {/* Personal message */}
      {message && (
        <div className="w-full p-2 rounded bg-muted/50 text-xs italic text-center mb-2 line-clamp-2">
          "{message}"
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 w-full mt-auto">
        <Button
          variant="outline"
          size="sm"
          onClick={onDecline}
          disabled={isActioning || expiryInfo.isExpired}
          className="flex-1 h-8 text-xs"
        >
          {isActioning ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <UserX className="h-3 w-3 mr-1" />
              Decline
            </>
          )}
        </Button>
        <Button
          size="sm"
          onClick={onAccept}
          disabled={isActioning || expiryInfo.isExpired}
          className={`flex-1 h-8 text-xs ${!expiryInfo.isExpired ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}`}
        >
          {isActioning ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <UserCheck className="h-3 w-3 mr-1" />
              Accept
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

/**
 * SentRequestCard - Card for sent connection requests
 */
interface SentRequestCardProps {
  request: SentConnectionRequest;
  onCancel: () => void;
  isCancelling: boolean;
}

const SentRequestCard: React.FC<SentRequestCardProps> = ({
  request,
  onCancel,
  isCancelling,
}) => {
  const { recipient, requestedAt, expiresAt } = request;
  const expiryInfo = getExpiryInfo(expiresAt);

  const displayName = recipient.firstName && recipient.lastName
    ? `${recipient.firstName} ${recipient.lastName}`
    : recipient.username;

  const initials = getInitials(
    recipient.firstName,
    recipient.lastName,
    recipient.username
  );

  return (
    <div className="relative flex flex-col items-center p-4 rounded-lg border bg-card shadow-sm hover:shadow-md transition-all duration-200 group">
      {/* Cancel button - top right */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        disabled={isCancelling}
        className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive hover:bg-destructive/10"
      >
        {isCancelling ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <X className="h-3 w-3" />
        )}
      </Button>

      {/* Avatar */}
      <Avatar className="h-14 w-14 ring-2 ring-background shadow mb-2">
        <AvatarImage src={recipient.avatar} alt={displayName} />
        <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
          {initials}
        </AvatarFallback>
      </Avatar>

      {/* Name and username */}
      <div className="text-center mb-1">
        <h3 className="font-medium text-sm line-clamp-1">{displayName}</h3>
        <p className="text-xs text-muted-foreground">@{recipient.username}</p>
      </div>

      {/* Sent date badge */}
      <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 mb-1">
        <Send className="h-2.5 w-2.5 mr-1" />
        Sent {formatRequestDate(requestedAt)}
      </Badge>

      {/* Expiry badge */}
      <Badge
        variant="outline"
        className={`text-[10px] px-1.5 py-0.5 mb-2 ${
          expiryInfo.isExpired
            ? 'border-destructive/50 text-destructive'
            : expiryInfo.isExpiringSoon
              ? 'border-yellow-500/50 text-yellow-600 dark:text-yellow-500'
              : ''
        }`}
      >
        {(expiryInfo.isExpired || expiryInfo.isExpiringSoon) && (
          <AlertCircle className="h-2.5 w-2.5 mr-1" />
        )}
        {expiryInfo.expiryText}
      </Badge>

      {/* Status badge */}
      <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 mb-2">
        <Clock className="h-2.5 w-2.5 mr-1" />
        Awaiting response
      </Badge>

      {/* Cancel button */}
      <Button
        variant="outline"
        size="sm"
        className="w-full mt-auto h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
        onClick={onCancel}
        disabled={isCancelling}
      >
        {isCancelling ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <>
            <X className="h-3 w-3 mr-1" />
            Cancel Request
          </>
        )}
      </Button>
    </div>
  );
};

/**
 * PendingConnectionRequests - Main component
 */
export const PendingConnectionRequests: React.FC<PendingConnectionRequestsProps> = ({
  defaultTab = 'received',
  onTabChange,
}) => {
  const pendingReceived = usePendingReceived();
  const pendingSent = usePendingSent();
  const pendingReceivedCount = usePendingReceivedCount();
  const pendingSentCount = usePendingSentCount();
  const { isLoading } = useConnectionLoading();
  const {
    fetchPendingReceived,
    fetchPendingSent,
    acceptRequest,
    declineRequest,
    cancelRequest,
  } = useConnectionActions();

  const [actioningId, setActioningId] = React.useState<string | null>(null);

  // Fetch pending requests on mount
  useEffect(() => {
    console.log('[PendingRequests] Fetching pending requests...');
    fetchPendingReceived();
    fetchPendingSent();
  }, [fetchPendingReceived, fetchPendingSent]);

  console.log('[PendingRequests] Rendering with', pendingReceivedCount, 'received,', pendingSentCount, 'sent');

  // Handle accept
  const handleAccept = async (connectionId: string) => {
    console.log('[PendingRequests] Accepting request:', connectionId);
    setActioningId(connectionId);
    try {
      await acceptRequest(connectionId);
    } finally {
      setActioningId(null);
    }
  };

  // Handle decline
  const handleDecline = async (connectionId: string) => {
    console.log('[PendingRequests] Declining request:', connectionId);
    setActioningId(connectionId);
    try {
      await declineRequest(connectionId);
    } finally {
      setActioningId(null);
    }
  };

  // Handle cancel
  const handleCancel = async (connectionId: string) => {
    console.log('[PendingRequests] Cancelling request:', connectionId);
    setActioningId(connectionId);
    try {
      await cancelRequest(connectionId);
    } finally {
      setActioningId(null);
    }
  };

  return (
    <Tabs
      defaultValue={defaultTab}
      onValueChange={onTabChange}
      className="w-full"
    >
      <TabsList className="grid w-full grid-cols-2 mb-4">
        <TabsTrigger value="received" className="relative">
          <Inbox className="h-4 w-4 mr-2" />
          Received
          {pendingReceivedCount > 0 && (
            <Badge className="ml-2 h-5 min-w-[20px] px-1.5 text-xs bg-primary text-primary-foreground">
              {pendingReceivedCount}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="sent" className="relative">
          <Send className="h-4 w-4 mr-2" />
          Sent
          {pendingSentCount > 0 && (
            <Badge
              variant="outline"
              className="ml-2 h-5 min-w-[20px] px-1.5 text-xs"
            >
              {pendingSentCount}
            </Badge>
          )}
        </TabsTrigger>
      </TabsList>

      {/* Received requests */}
      <TabsContent value="received">
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex flex-col items-center p-4 rounded-lg border bg-card">
                <div className="h-14 w-14 rounded-full bg-muted mb-2 animate-pulse" />
                <div className="h-4 w-24 bg-muted rounded mb-1 animate-pulse" />
                <div className="h-3 w-16 bg-muted rounded mb-2 animate-pulse" />
                <div className="h-5 w-28 bg-muted rounded mb-2 animate-pulse" />
                <div className="h-8 w-full bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : pendingReceived.length === 0 ? (
          <div className="text-center py-12">
            <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
              <Inbox className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-medium mb-2">No pending requests</h3>
            <p className="text-muted-foreground">
              Connection requests you receive will appear here
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {pendingReceived.map((request) => (
              <ReceivedRequestCard
                key={request.connectionId}
                request={request}
                onAccept={() => handleAccept(request.connectionId)}
                onDecline={() => handleDecline(request.connectionId)}
                isActioning={actioningId === request.connectionId}
              />
            ))}
          </div>
        )}
      </TabsContent>

      {/* Sent requests */}
      <TabsContent value="sent">
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex flex-col items-center p-4 rounded-lg border bg-card">
                <div className="h-14 w-14 rounded-full bg-muted mb-2 animate-pulse" />
                <div className="h-4 w-24 bg-muted rounded mb-1 animate-pulse" />
                <div className="h-3 w-16 bg-muted rounded mb-2 animate-pulse" />
                <div className="h-5 w-28 bg-muted rounded mb-2 animate-pulse" />
                <div className="h-8 w-full bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : pendingSent.length === 0 ? (
          <div className="text-center py-12">
            <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
              <Send className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-medium mb-2">No sent requests</h3>
            <p className="text-muted-foreground">
              Requests you send will appear here until accepted or declined
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {pendingSent.map((request) => (
              <SentRequestCard
                key={request.connectionId}
                request={request}
                onCancel={() => handleCancel(request.connectionId)}
                isCancelling={actioningId === request.connectionId}
              />
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};

export default PendingConnectionRequests;
